<template>
  <div class="profit-loss-widget-container">
    <div v-if="loading" class="profit-loss-widget-loading">Loading...</div>
    <div v-else-if="error" class="profit-loss-widget-error">{{ error }}</div>
    <div v-else class="profit-loss-widget-content">
      <div class="profit-loss-widget-header">
        <div class="profit-loss-widget-header-label">Net Profit</div>
        <div 
          class="profit-loss-widget-header-value" 
          :class="netProfit >= 0 ? 'profit-loss-widget-positive' : 'profit-loss-widget-negative'"
        >
          {{ formatCurrency(netProfit) }}
        </div>
      </div>
      <div class="profit-loss-widget-details">
        <div class="profit-loss-widget-detail-row">
          <div class="profit-loss-widget-detail-label">Total Income</div>
          <div class="profit-loss-widget-detail-value profit-loss-widget-positive">{{ formatCurrency(totalIncome) }}</div>
        </div>
        <div class="profit-loss-widget-detail-row">
          <div class="profit-loss-widget-detail-label">Total Expenses</div>
          <div class="profit-loss-widget-detail-value profit-loss-widget-negative">{{ formatCurrency(totalExpenses) }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import './styles/ProfitLossWidget.css';
import ApiService from '@/services/api.service';
import logger from '@/utils/logger';

export default {
  name: 'ProfitLossWidget',
  props: {
    companyId: { type: [String, Number], required: true }
  },
  data() {
    return {
      totalIncome: 0,
      totalExpenses: 0,
      loading: true,
      error: null
    };
  },
  computed: {
    netProfit() {
      return this.totalIncome - this.totalExpenses;
    }
  },
  async mounted() {
    await this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      this.error = null;
      try {
        // Fetch income accounts
        const accountsResponse = await ApiService.get(`/company-account/company/${this.companyId}`);
        const accountsData = Array.isArray(accountsResponse.data) ? accountsResponse.data : (accountsResponse.data?.accounts || []);
        
        // Calculate total income from income accounts
        const incomeTypes = ['Income', 'Other Income'];
        this.totalIncome = accountsData
          .filter(acc => acc.active !== false && incomeTypes.includes(acc.accountType))
          .reduce((sum, acc) => sum + parseFloat(acc.currentBalance || acc.balance || 0), 0);
        
        // Fetch expenses
        const expenses = await ApiService.getBillsAndExpenses(this.companyId, { view: 'Expenses' });
        const expensesArray = Array.isArray(expenses) ? expenses : [];
        
        // Filter to only include expenses (not bills) and exclude voided/cancelled
        const validExpenses = expensesArray.filter(expense => {
          return expense.type === 'Expense' &&
                 !expense.voided &&
                 expense.status !== 'voided' &&
                 expense.status !== 'cancelled';
        });
        
        // Sum all expense amounts
        this.totalExpenses = validExpenses.reduce((sum, expense) => {
          return sum + parseFloat(expense.originalAmount || expense.totalAmount || 0);
        }, 0);
      } catch (error) {
        logger.error('Error fetching profit and loss data:', error);
        this.error = 'Failed to load data';
      } finally {
        this.loading = false;
      }
    },
    formatCurrency(amount) {
      if (amount === null || amount === undefined || isNaN(amount)) return '$0.00';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    }
  }
};
</script>

